import gql from "graphql-tag";

export const ClientProfileCompact = gql`
  fragment ClientProfileCompact on ClientProfile {
    id
    companyName
  }
`;

export const VendorProfileCompact = gql`
  fragment VendorProfileCompact on VendorProfile {
    id
    companyName
  }
`;

export const ClientProfile = gql`
  fragment ClientProfile on ClientProfile {
    id
    commissionPercentage
    companyName
    logoUrl
    clientCustomAgreementUrl
    vendorCustomAgreementUrl
    isDefault
    isDefaultVendorEnabled
    isS3UploadEnabled
    isHttpUploadEnabled
    autoIncrementBidDeadlineDefaultValue
    isFtpUploadEnabledForDelivery
    isDefault
    canAssignTasks
    isTeamLeadFeatureEnabled
    authorizedForOfflineAccess
  }
`;

export const VendorProfile = gql`
  fragment VendorProfile on VendorProfile {
    id
    commissionPercentage
    companyName
    logoUrl
    clientCustomAgreementUrl
    vendorCustomAgreementUrl
    isDefault
    isDefaultVendorEnabled
    isS3UploadEnabled
    isHttpUploadEnabled
    autoIncrementBidDeadlineDefaultValue
    isFtpUploadEnabledForDelivery
    isDefault
    canAssignTasks
    isTeamLeadFeatureEnabled
    authorizedForOfflineAccess
  }
`;

export const CompanyNameData = gql`
  fragment CompanyNameData on Company {
    id
    profile {
      ...ClientProfile
      ...VendorProfile
    }
  }
  ${ClientProfile}
  ${VendorProfile}
`;

export const CreatorData = gql`
  fragment CreatorData on User {
    role
    dateJoined
    inviter {
      id
      fullName
      firstName
      lastName
    }
    lastLogin
    invitationExpiresInSeconds
    invitationRevokedBy {
      id
      firstName
      lastName
    }
    clientUserServiceAlertRules {
      edges {
        node {
          id
          action
          type
          maxPerToLowerBid
          serviceId
        }
      }
    }
    timezone
    id
    username
    firstName
    lastName
    fullName
    email
    isActive
    profilePictureUrl
    collaboratorEmail
    office {
      id
      city
      name
      isCustomDeliveryEnabled
      hasCustomNamingRequirement
      hasDefaultNamingRequirement
      company {
        id
        ...CompanyNameData
      }
      timezoneName
      timezone
      bidRequestCurrency
    }
  }

  ${CompanyNameData}
`;

export const StateChangeVersionsData = gql`
  fragment StateChangeVersionsData on TSBLatestVersions {
    approvedRevision {
      revisionDateCreated
      amount
      billedAmount
      displayAmount
      deliveryDate
      status
      currentBidPackageId
      manDays
    }
    clientRevision {
      revisionDateCreated
      amount
      billedAmount
      displayAmount
      costSplits {
        service
        amount
      }
      deliveryDate
      status
      currentBidPackageId
      deliverables {
        previous {
          id
          format {
            id
            format
          }
          spec {
            id
            specification
          }
          comments
        }
        new {
          id
          format {
            id
            format
          }
          spec {
            id
            specification
          }
          comments
        }
      }
      additionalReferencesAttachments {
        bidPackageId
        fileAttachments {
          id
          url
          key
          name
        }
      }
    }
    vendorRevision {
      revisionDateCreated
      amount
      billedAmount
      displayAmount
      costSplits {
        service
        amount
      }
      deliveryDate
      status
      currentBidPackageId
    }
  }
`;

export const TaskShotBidData = gql`
  fragment TaskShotBidData on TaskShotBid {
    id
    vendor {
      id
      ...CompanyNameData
    }
    currentBidPackage {
      id
      action
    }
    amount
    costSplits {
      service
      amount
    }
    manDaysSplits {
      service
      manDays
    }
    manDays
    billedAmount
    displayAmount
    latestVersions {
      ...StateChangeVersionsData
    }
    deliveryDate
    taskShotId
    status
    updated
  }

  ${StateChangeVersionsData}
`;

export const DashboardBidData = gql`
  fragment DashboardBidData on TaskShotBid {
    id
    cancelledDate
    deliveryDate
    status
    vendor {
      id
    }
    latestVendorDelivery {
      id
      created
    }
  }
`;

export const VendorBidRequestData = gql`
  fragment VendorBidRequestData on VendorBidRequest {
    id
    commentCount
    status
    s3PackageUrl
    artifactsPackageName
    vendor {
      id
      ...CompanyNameData
    }
    status
  }
`;

export const BidRequestData = gql`
  fragment BidRequestData on BidRequest {
    id
    clientEndOfDay
    currency
    created
    deadline
    isAsap
    taskId
    bidFormat
    previewVideoUrls {
      shotId
      videoUrl
    }
    task {
      id
      project {
        id
      }
    }
    originalBidPackage {
      id
    }
    creator {
      id
      ...CreatorData
    }
    vendorBidRequests {
      id
      vendor {
        id
        ...CompanyNameData
      }
    }
    sourceBidRequest {
      id
      task {
        id
        project {
          id
        }
      }
      originalBidPackage {
        id
      }
    }

    primaryClientOffice {
      id
      timezone
      enablePreviewVideos
    }
  }
  ${CreatorData}
  ${CompanyNameData}
`;

export const BidPackageTaskData = gql`
  fragment BidPackageTaskData on BidPackageTask {
    id
    task {
      id
      service {
        id
      }
    }
    notes
    attachments {
      id
      url
      key
      name
    }
  }
`;

export const AttachmentData = gql`
  fragment AttachmentData on FileAttachment {
    id
    url
    key
    name
    isArchivedOnS3
  }
`;

export const InvoiceCommentData = gql`
  fragment InvoiceCommentData on InvoiceComment {
    id
    comment
    attachments {
      ...AttachmentData
    }
    user {
      id
      username
      ...CreatorData
    }
    created
  }
  ${AttachmentData}
  ${CreatorData}
`;

export const VendorBidRequestCommentData = gql`
  fragment VendorBidRequestCommentData on VendorBidRequestComment {
    __typename
    id
    comment
    created
    attachments {
      ...AttachmentData
    }
    sharedComments {
      id
      comment
      created
      attachments {
        ...AttachmentData
      }
      user {
        id
        firstName
        lastName
      }
      vendorBidRequest {
        id
        vendor {
          id
          ...CompanyNameData
        }
      }
    }
    user {
      id
      username
      ...CreatorData
    }
    isEmailSent
  }

  ${CreatorData}
  ${AttachmentData}
`;

export const TaskShotBidCommentData = gql`
  fragment TaskShotBidCommentData on TaskShotBidComment {
    id
    comment
    created
    attachments {
      ...AttachmentData
    }
    sharedComment {
      id
      comment
      created
      type
      attachments {
        ...AttachmentData
      }
      user {
        id
        firstName
        lastName
      }
      taskShotBid {
        id
        vendor {
          id
          ...CompanyNameData
        }
      }
    }
    user {
      id
      username
      ...CreatorData
    }
    origin
    originEntityType
    originEntityId
  }
  ${CreatorData}
  ${AttachmentData}
`;

export const PaymentDetailData = gql`
  fragment PaymentDetailData on PaymentDetails {
    id
    bankAddress
    transferTo
    bankName
    routingNumber
    transitNumber
    institutionNumber
    accountNumber
    recipientAddress
    recipientAddress2
    recipientAddress3
    recipientCity
    swiftCodeNumber
    recipientState
    recipientZipcode
    bankAddress
    bankCity
    bankState
    bankZipcode
    recipientCountry {
      id
      name
    }
  }
`;

export const SubcontractedDeliveriesData = gql`
  fragment SubcontractedDeliveriesData on VendorDelivery {
    id
    created
    deliveredFiles
    creator {
      id
      timezoneName
      ...CreatorData
    }
    deliveryFilesToSourceTaskShotIds {
      key
      taskShotId
      isBroken
    }
    infectedFilesToSourceTaskShotIds {
      error
      taskShotId
    }
    vendor {
      id
      ...CompanyNameData
    }
  }
  ${CompanyNameData}
  ${CreatorData}
`;

export const TaskShotsData = gql`
  fragment TaskShotsData on TaskShot {
    id
    duplicateTaskShots {
      id
      task {
        id
        service {
          id
          name
        }
      }
    }
    description
    attachments {
      id
      ...AttachmentData
    }
    subService {
      id
      name
    }
    customDeliveries {
      id
      status
      deliveryPackageName
      deliveryPackagePath
    }
    tags {
      id
      name
      color
      isSmart
    }
    systemTags {
      id
      name
      color
    }
    awardedBid {
      id
      currentBidPackage {
        id
        action
      }
      awardedDate
      deliveryDate
      formattedDeliveryDate
      displayAmount
      latestVersionNumber
      status
      vendor {
        id
        ...CompanyNameData
      }
      latestVendorDelivery {
        id
        created
        deliveryUrlToTaskShotIds {
          url
          taskShotId
          isBroken
        }
      }
      invoice {
        id
      }
      taskShot {
        id
      }
      bidRequest {
        id
        created
        deadline
        primaryClientOffice {
          id
          timezone
          enablePreviewVideos
        }
        previewVideoUrls {
          shotId
          videoUrl
        }
        created
        initiator {
          id
          fullName
        }
        vendorBidRequests {
          id
          previewVideoUrls {
            shotId
            videoUrl
          }
        }
      }
    }
    userTaskShots {
      id
      user {
        id
        firstName
        lastName
      }
      userDeliveries {
        deliveryPath
        deliveryDate
      }
      vendorProject {
        id
      }
      isArtistLead
    }
    subcontractedTaskShot {
      id
      awardedBid {
        id
        vendor {
          id
          ...CompanyNameData
        }
        deliveryDate
        formattedDeliveryDate
      }
    }
    subcontractedDeliveries {
      id
      ...SubcontractedDeliveriesData
    }
    task {
      id
      creator {
        id
        ...CreatorData
      }
      project {
        id
        name
        sourceProject {
          id
        }
        clientCode
        lastThreeProjects {
          id
          name
        }
        securityLevel
        creator {
          id
          ...CreatorData
        }
      }
      service {
        id
        name
      }
    }
    turnover {
      id
      created
    }
  }

  ${CreatorData}
  ${SubcontractedDeliveriesData}
  ${AttachmentData}
`;

export const DefaultBillingAddressData = gql`
  fragment DefaultBillingAddressData on BillingAddress {
    id
    address
    country {
      id
      name
    }
    state
    city
    zipcode
  }
`;

export const InvoiceData = gql`
  fragment InvoiceData on Invoice {
    id
    commentCount
    feesByService {
      serviceName
      cost
      totalCost
      commissionPercentage
    }
    created
    terms
    notes
    other
    isPaid
    dueDate
    invoiceNumber
    canDirectDownload
    totalCost
    recipientOffice {
      id
      name
      address
      country {
        id
        name
      }
      state
      city
      zipcode
    }
    totalCostWithFees
    project {
      id
      name
      creator {
        id
        timezone
      }
    }
    comments {
      id
      ...InvoiceCommentData
    }
    paymentDetails {
      id
      ...PaymentDetailData
    }
    payments {
      id
      amount
      paymentDate
      notes
    }
    taskShotBids(first: $limit, after: $endCursor) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          invoicedVersion(invoiceId: $invoiceId) {
            id
            displayAmount
            billedAmount
            deliveryDate
            latestVendorDelivery {
              id
              created
            }
            taskShot {
              awardedBid {
                id
                bidRequest {
                  id
                  primaryClientOffice {
                    id
                    timezone
                  }
                }
              }
              id
              subService {
                id
                name
              }
              tags {
                id
                name
                color
                isSmart
              }
              shot {
                id
                name
              }
              task {
                id
                service {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
    client {
      id
      ...CompanyNameData
      defaultBillingAddress {
        id
        ...DefaultBillingAddressData
      }
    }
    vendor {
      id
      ...CompanyNameData
      defaultBillingAddress {
        id
        ...DefaultBillingAddressData
      }
    }
  }
  ${CompanyNameData}
  ${PaymentDetailData}
  ${DefaultBillingAddressData}
  ${InvoiceCommentData}
`;

export const UserTagData = gql`
  fragment UserTagData on UserTag {
    id
    name
    color
  }
`;

export const NoteTypeData = gql`
  fragment NoteType on NoteType {
    name
    id
  }
`;

export const ClientVendorCommentData = gql`
  fragment ClientVendorCommentData on ClientVendorComment {
    id
    comment
    created
    sharedComments {
      id
      comment
      created
      attachments {
        id
        ...AttachmentData
      }
      creator {
        id
        firstName
        lastName
        profilePictureUrl
      }
      vendor {
        id
        role
        profile {
          ...ClientProfile
          ...VendorProfile
        }
      }
    }
    client {
      id
      type
      role
      profile {
        ...ClientProfile
        ...VendorProfile
      }
    }
    creator {
      id
      ...CreatorData
    }
    vendor {
      id
      role
      profile {
        ...ClientProfile
        ...VendorProfile
      }
    }
    attachments {
      id
      ...AttachmentData
    }
    slackMessageCreator
    slackMessageId
  }
  ${ClientProfile}
  ${VendorProfile}
  ${CreatorData}
  ${AttachmentData}
`;

export const VendorUserCommentData = gql`
  fragment VendorUserCommentData on VendorUserComment {
    id
    comment
    created
    meta
    creator {
      id
      ...CreatorData
    }
    user {
      id
      userCity
      timezone
      username
      firstName
      lastName
    }
    vendor {
      id
      role
      profile {
        ...VendorProfile
        ...ClientProfile
      }
    }
    attachments {
      id
      ...AttachmentData
    }
  }
  ${VendorProfile}
  ${CreatorData}
  ${AttachmentData}
`;

export const UserTaskShotCommentData = gql`
  fragment UserTaskShotCommentData on UserTaskShotComment {
    id
    comment
    created
    creator {
      id
      ...CreatorData
    }
    attachments {
      id
      ...AttachmentData
    }
  }
  ${CreatorData}
  ${AttachmentData}
`;

export const TaskShotBidDataForNotes = gql`
  fragment TaskShotBidDataForNotes on TaskShotBid {
    id
    currentBidPackage {
      id
      action
    }
    latestVersions {
      ...StateChangeVersionsData
    }
    taskShotId
    taskShot {
      id
      latestDeliveryVersion
    }
  }

  ${StateChangeVersionsData}
`;

export const ProjectDetailData = gql`
  fragment ProjectDetail on Project {
    id
    isArchived
    userNotificationSubscriptions
    name
    vendorFtpDirectory
    sourceFtpDirectory
    unmappedShotsCount
    vendorProject {
      id
    }
    projectCategory {
      id
      name
      type
    }
    slapshotProjectId
    showEnableAiButton
    isAiEnabled
    subscribedUsers {
      id
      firstName
      lastName
      email
      role
      isActive
      dateJoined
      lastLogin
      inviter {
        id
        firstName
        lastName
      }
      invitationExpiresInSeconds
      invitationRevokedBy {
        id
        firstName
        lastName
      }
      clientUserServiceAlertRules {
        edges {
          node {
            id
            action
            type
            maxPerToLowerBid
            serviceId
          }
        }
      }
    }
    sourceProject {
      id
      hasCustomNamingRequirement
      hasDefaultNamingRequirement
      creator {
        ...CreatorData
      }
    }
    subcontractedProject {
      id
    }
    hasCompletedTasks
    creator {
      ...CreatorData
    }
    securityLevel
    created
  }

  ${CreatorData}
`;

export const VendorProjectData = gql`
  fragment VendorProject on VendorProject {
    id
    isCurrentUserArtistLead
    project {
      id
      name
      securityLevel
    }
    company {
      id
      profile {
        ...VendorProfileCompact
      }
    }
  }

  ${VendorProfileCompact}
`;
